import { CONFIGS_BASE_URL } from "../../../constants";
import { getToken, isLoggedIn } from "../../../context/authContext";
import { AuthConfig } from "../../../context/config/authConfig";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import { PROMOTIONS_PATH } from "./constants";
import {
  IAdvertisementsListResponse,
  IAdvertisementResponse,
  ICreateAdvertisementBody,
  IUpdateAdvertisementBody,
  IAdvertisementsParams,
  IAdvertisement,
  EAdvertisementStatus,
} from "./models";

const appProvide = async () => AuthConfig.getAppToken();
const defaultProvide = async () => await getToken();

/**
 * Get all advertisements with optional filtering and pagination
 */
export const getAdvertisements = ({
  page = 0,
  size = 10,
  sort = "updatedAt,desc",
  searchKeyWord = "",
  status,
  platform,
}: IAdvertisementsParams = {}): Promise<IAdvertisementsListResponse> => {
  let url = `${CONFIGS_BASE_URL}${PROMOTIONS_PATH}`;

  // Add query parameters if provided
  const params = new URLSearchParams();
  if (page > 0) params.append("page", page.toString());
  if (size !== 10) params.append("size", size.toString());
  if (sort !== "updatedAt,desc") params.append("sort", sort);
  if (searchKeyWord) params.append("searchKeyWord", searchKeyWord);
  if (status) params.append("status", status);
  if (platform) params.append("platform", platform);

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const descriptor = new ApiServiceManager(
    url,
    "GET",
    isLoggedIn() ? defaultProvide : appProvide
  );

  return fetchRequest(descriptor, null);
};

/**
 * Get a specific advertisement by ID
 */
export const getAdvertisementById = async (
  id: string
): Promise<IAdvertisement> => {
  const descriptor = new ApiServiceManager(
    `${CONFIGS_BASE_URL}${PROMOTIONS_PATH}/${id}`,
    "GET",
    isLoggedIn() ? defaultProvide : appProvide
  );

  const response: IAdvertisementResponse = await fetchRequest(descriptor, null);
  return response.data;
};

/**
 * Create a new advertisement
 */
export const createAdvertisement = async (
  body: ICreateAdvertisementBody
): Promise<IAdvertisement> => {
  const descriptor = new ApiServiceManager(
    `${CONFIGS_BASE_URL}${PROMOTIONS_PATH}`,
    "POST",
    defaultProvide
  );

  const response: IAdvertisementResponse = await fetchRequest(descriptor, body);
  return response.data;
};

/**
 * Update an existing advertisement
 */
export const updateAdvertisement = async (
  id: string,
  body: IUpdateAdvertisementBody
): Promise<IAdvertisement> => {
  const descriptor = new ApiServiceManager(
    `${CONFIGS_BASE_URL}${PROMOTIONS_PATH}/${id}`,
    "PUT",
    defaultProvide
  );

  const response: IAdvertisementResponse = await fetchRequest(descriptor, body);
  return response.data;
};

/**
 * Delete an advertisement by ID
 */
export const deleteAdvertisement = (id: string): Promise<void> => {
  const descriptor = new ApiServiceManager(
    `${CONFIGS_BASE_URL}${PROMOTIONS_PATH}/${id}`,
    "DELETE",
    defaultProvide
  );

  return fetchRequest(descriptor, null);
};

/**
 * Toggle advertisement status (active/inactive)
 */
export const toggleAdvertisementStatus = async (
  id: string,
  status: EAdvertisementStatus
): Promise<IAdvertisement> => {
  const descriptor = new ApiServiceManager(
    `${CONFIGS_BASE_URL}${PROMOTIONS_PATH}/${id}/status/${status}`,
    "PATCH",
    defaultProvide
  );

  const response: IAdvertisementResponse = await fetchRequest(descriptor, null);
  return response.data;
};

/**
 * Reorder advertisements
 */
export const reorderAdvertisements = (orderedIds: string[]): Promise<void> => {
  const descriptor = new ApiServiceManager(
    `${CONFIGS_BASE_URL}${PROMOTIONS_PATH}/reorder`,
    "POST",
    defaultProvide
  );

  return fetchRequest(descriptor, { orderedIds });
};
